import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Truck, Plus, MapPin, Loader2, Clock, CheckCircle2,
  ArrowRight, Star, Search, ShieldAlert, UserCheck, BadgeCheck, Send, User, X
} from "lucide-react";

import Sidebar   from "../Includes/Sidebar";
import TopBar    from "../Includes/TopBar";
import BottomNav from "../Includes/BottomNav";
import LoadPayBreakdown, { payHeadline } from "../../Loads/LoadPayBreakdown";

// ─── UTILITIES & SMALL COMPONENTS ──────────────────────────────────────────────

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const fmtDate = (d, opts = { weekday:"short", month:"short", day:"numeric" }) =>
  d ? new Date(d).toLocaleDateString("en-US", opts) : "—";

const fmt2 = (n) => parseFloat(n || 0).toFixed(2);

function StatusPill({ status, isOpen }) {
  if (isOpen) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
        <Clock className="w-2.5 h-2.5" /> Pending Match
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 whitespace-nowrap">
      <CheckCircle2 className="w-2.5 h-2.5" /> Assigned
    </span>
  );
}

// ─── ROUTE MAP ────────────────────────────────────────────────────────────────
function RouteMap({ origin, destination, originCoords, destCoords, meetingCoords, meetingName, distanceMiles }) {
  const fromCoords = originCoords || [39.8283, -98.5795];
  const toCoords   = destCoords || [39.8283, -98.5795];

  const midLat  = (fromCoords[0] + toCoords[0]) / 2;
  const midLng  = (fromCoords[1] + toCoords[1]) / 2;
  const dLat    = toCoords[0] - fromCoords[0];
  const dLng    = toCoords[1] - fromCoords[1];
  const distMi  = distanceMiles ? Math.round(distanceMiles) : (() => { const r = (x) => (x * Math.PI) / 180; const h = Math.sin(r(dLat) / 2) ** 2 + Math.cos(r(fromCoords[0])) * Math.cos(r(toCoords[0])) * Math.sin(r(dLng) / 2) ** 2; return Math.round(2 * 3958.8 * Math.asin(Math.sqrt(h)) * 1.18); })();
  const zoom = distMi < 80 ? 8 : distMi < 200 ? 7 : distMi < 400 ? 6 : 5;

  const mapHtml = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css"/>
<script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"></script>
<style>*{margin:0;padding:0;box-sizing:border-box;}html,body,#map{width:100%;height:100%;}</style>
</head><body>
<div id="map"></div>
<script>
var map = L.map('map', {zoomControl:true, attributionControl:false}).setView([${midLat},${midLng}],${zoom});
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:18}).addTo(map);

var fromPt = [${fromCoords[0]},${fromCoords[1]}];
var toPt   = [${toCoords[0]},${toCoords[1]}];
var dLat = toPt[0] - fromPt[0];
var dLng = toPt[1] - fromPt[1];
var pts = [];
var steps = 20;
for(var i=0;i<=steps;i++){
  var t = i/steps;
  var cx = (fromPt[1]+toPt[1])/2 + dLat*0.15;
  var cy = (fromPt[0]+toPt[0])/2 - dLng*0.15;
  var lat = (1-t)*(1-t)*fromPt[0] + 2*(1-t)*t*cy + t*t*toPt[0];
  var lng = (1-t)*(1-t)*fromPt[1] + 2*(1-t)*t*cx + t*t*toPt[1];
  pts.push([lat,lng]);
}

L.polyline(pts,{color:'#0099a8',weight:4,opacity:0.85,dashArray:null}).addTo(map);
L.polyline(pts,{color:'#005f6b',weight:6,opacity:0.2}).addTo(map);

var originIcon = L.divIcon({html:'<div style="background:#e07b00;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',iconAnchor:[7,7],className:''});
var destIcon   = L.divIcon({html:'<div style="background:#0099a8;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"></div>',iconAnchor:[9,9],className:''});

L.marker(fromPt,{icon:originIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${origin.replace(/'/g,"\\'")}</b>',{permanent:false,direction:'top'});
L.marker(toPt,  {icon:destIcon  }).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${destination.replace(/'/g,"\\'")}</b>',{permanent:false,direction:'top'});

${meetingCoords && meetingCoords[0] ? `
var meetingPt = [${meetingCoords[0]},${meetingCoords[1]}];
var meetIcon = L.divIcon({html:'<div style="background:#7c3aed;width:14px;height:14px;border:3px solid white;border-radius:3px;box-shadow:0 2px 6px rgba(0,0,0,0.4);transform:rotate(45deg);"></div>',iconAnchor:[7,7],className:''});
var meetName = "${meetingName ? meetingName.split(",")[0].replace(/"/g, '\\"').replace(/'/g, "\\'") : 'Meeting Point'}";
L.marker(meetingPt,{icon:meetIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">🚩 ' + meetName + '</b>',{permanent:true,direction:'top', offset:[0,-10]});
var bounds = L.latLngBounds([fromPt, toPt, meetingPt]);
map.fitBounds(bounds, {padding: [30, 30]});
` : ''}
</script>
</body></html>`;

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-inner bg-gray-50 h-48 sm:h-56 relative w-full">
      <iframe srcDoc={mapHtml} style={{ width:"100%", height:"100%", border:"none", display:"block" }} title="Route Map" />
      <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur border border-gray-200/50 rounded-lg p-2 flex items-center justify-between pointer-events-none shadow-sm">
         <span className="text-[10px] font-bold text-gray-700 truncate max-w-[40%]">📍 {origin.split(",")[0]}</span>
         <span className="text-[10px] font-bold text-blue-600">~{distMi} miles</span>
         <span className="text-[10px] font-bold text-gray-700 truncate max-w-[40%] text-right">🏁 {destination.split(",")[0]}</span>
      </div>
    </div>
  );
}

// ─── INVITE VERIFIED DRIVERS MODAL ──────────────────────────────────────────────
function InviteDriversModal({ load, onClose }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  // Initialize with IDs from backend so buttons load in correct state
  const [invitedIds, setInvitedIds] = useState(load.invited_driver_ids || []);
  const [invitingId, setInvitingId] = useState(null);

  const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");

  useEffect(() => {
    const fetchVerifiedDrivers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/drivers/verified/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setDrivers(data);
        }
      } catch (err) {
        console.error("Error fetching verified drivers:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchVerifiedDrivers();
  }, [token]);

  const handleSendInvite = async (driverId) => {
    setInvitingId(driverId);
    try {
      const res = await fetch(`${API_URL}/api/loads/${load.pk || load.id}/invite/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ driver_id: driverId })
      });

      if (res.ok) {
        // Update local state to trigger button change
        setInvitedIds(prev => [...prev, driverId]);
        
        // Mutate the load object so if they close and reopen the modal, it stays invited
        if (!load.invited_driver_ids) {
            load.invited_driver_ids = [];
        }
        if (!load.invited_driver_ids.includes(driverId)) {
            load.invited_driver_ids.push(driverId);
        }
      } else {
        const data = await res.json();
        alert(data.error || data.message || "Could not send invitation");
      }
    } catch (err) {
      alert("Network error sending invitation");
    } finally {
      setInvitingId(null);
    }
  };

  const filtered = drivers.filter(d => 
    !search || 
    d.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    d.location?.toLowerCase().includes(search.toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
        
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100" style={{ background: "#1a2744" }}>
          <div>
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-green-400" /> Invite Verified Drivers
            </h2>
            <p className="text-[11px] text-white/60 mt-0.5">
              Load: {load.from || load.origin} → {load.to || load.destination}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-3 border-b border-gray-100 bg-gray-50">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input 
              type="text"
              placeholder="Search verified drivers by name or state..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        {/* Drivers List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-7 h-7 animate-spin text-blue-500 mb-2" />
              <p className="text-xs text-gray-400 font-medium">Fetching verified pilot drivers...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10">
              <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-bold text-gray-700">No verified drivers found</p>
              <p className="text-xs text-gray-400 mt-1">Try broadening your search term.</p>
            </div>
          ) : (
            filtered.map(driver => {
              const isInvited = invitedIds.includes(driver.id);
              const isPendingInvite = invitingId === driver.id;

              return (
                <div key={driver.id} className="p-3.5 bg-white border border-gray-100 rounded-xl hover:shadow-sm transition flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 bg-blue-50 text-blue-700 font-extrabold text-xs rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-100">
                      {driver.initials || "DR"}
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-gray-900 truncate flex items-center gap-1.5">
                        {driver.full_name}
                        {driver.is_badge_verified && (
                          <BadgeCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        )}
                      </h4>
                      <p className="text-[11px] text-gray-400 truncate mt-0.5">
                        {driver.vehicle || "Pilot Car"} · {driver.location}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-semibold text-amber-600 flex items-center gap-0.5">
                          <Star className="w-3 h-3 fill-amber-500 stroke-amber-500" /> {driver.rating || "4.9"}
                        </span>
                        <span className="text-[10px] text-gray-300">•</span>
                        <span className="text-[10px] text-gray-500 font-medium">{driver.experience_years || 5} yrs exp</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleSendInvite(driver.id)}
                    disabled={isInvited || isPendingInvite}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 flex-shrink-0 ${
                      isInvited 
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-[#1a2744] text-white hover:opacity-90 disabled:opacity-50"
                    }`}
                  >
                    {isPendingInvite ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : isInvited ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    {isInvited ? "Invited" : "Send Invite"}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-200 text-xs font-bold text-gray-700 rounded-xl hover:bg-gray-100 transition">
            Done
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}

// ─── PREMIUM LOAD MODAL ────────────────────────────────────────────────────────
function LoadModal({ load, onClose }) {
  const navigate = useNavigate();

  if (!load) return null;

  const isOpen = load.status?.toLowerCase() === "open";
  
  const commodity = load.type || load.commodity || load.load_type || "—";
  const weight = load.weight || (load.weight_lbs ? `${load.weight_lbs} lbs` : null) || "N/A";
  

  const origin = load.from || load.origin || load.origin_label || "—";
  const destination = load.to || load.destination || load.destination_label || "—";
  
  const hasMap = load.startCoords && load.endCoords;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background:"rgba(0,0,0,0.65)", backdropFilter:"blur(3px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] sm:max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 flex-shrink-0" style={{ background:"#1a2744" }}>
          <div className="flex-1 min-w-0 pr-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-white truncate">Load Details</h3>
              {load.is_featured && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">
                  ⚡ Featured
                </span>
              )}
            </div>
            <p className="text-[10px] text-white/40 mt-0.5 truncate">
              #{load.id || load.reference_code} · {isOpen ? "Pending Driver Match" : "Driver Assigned"}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-colors flex-shrink-0">✕</button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-5 space-y-5 sm:space-y-6">
          
          {/* Map & Route */}
          <div>
            {hasMap ? (
              <div className="mb-4">
                 <RouteMap 
                    origin={origin} 
                    destination={destination} 
                    originCoords={load.startCoords} 
                    destCoords={load.endCoords}
                    meetingCoords={load.meetingCoords}
                    meetingName={load.meeting_point}
                    distanceMiles={load.distance_miles}
                 />
              </div>
            ) : null}
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
               <div className="flex-1 min-w-0">
                 <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Origin</p>
                 <p className="text-sm font-bold text-gray-900 leading-snug break-words">{origin}</p>
                 {load.origin_zip && <p className="text-xs text-gray-500 mt-0.5">ZIP: {load.origin_zip}</p>}
                 <p className="text-xs font-semibold text-blue-600 mt-2">
                   📅 {load.date || "TBD"} {load.time ? `· 🕐 ${load.time}` : ""}
                 </p>
               </div>
               <ArrowRight className="hidden sm:block w-5 h-5 text-gray-300 flex-shrink-0" />
               <div className="flex-1 min-w-0 sm:text-right border-t sm:border-t-0 border-gray-200 pt-3 sm:pt-0">
                 <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Destination</p>
                 <p className="text-sm font-bold text-gray-900 leading-snug break-words">{destination}</p>
                 {load.destination_zip && <p className="text-xs text-gray-500 mt-0.5">ZIP: {load.destination_zip}</p>}
               </div>
            </div>
          </div>

          {/* Meeting Point */}
          {load.meeting_point && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-purple-500 mb-2">🚩 Meeting Point</p>
              <div className="bg-purple-50/50 border border-purple-100 rounded-xl p-4">
                <p className="text-sm font-bold text-gray-900 break-words">{load.meeting_point}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-600">
                  {load.meeting_city && <span>{load.meeting_city}, {load.meeting_state} {load.meeting_zip}</span>}
                  {load.meeting_time && <span className="font-semibold text-purple-700">Time: {load.meeting_time}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Load Specs & Escorts Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Load Specs</p>
              <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 space-y-3">
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-2">
                  <span className="text-xs text-gray-500 flex-shrink-0">Commodity</span>
                  <span className="text-xs font-bold text-gray-900 text-right">{commodity}</span>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-2">
                  <span className="text-xs text-gray-500 flex-shrink-0">Dimensions</span>
                  <span className="text-xs font-bold text-gray-900 text-right">{load.dims || "—"}</span>
                </div>
                <div className="flex justify-between gap-2 border-b border-gray-100 pb-2">
                  <span className="text-xs text-gray-500 flex-shrink-0">Weight</span>
                  <span className="text-xs font-bold text-gray-900 text-right">{weight}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Required Escorts ({load.escorts})</p>
              <div className="flex flex-wrap gap-2">
                 {load.needs_front_escort && <span className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold">Front Escort</span>}
                 {load.needs_chase_escort && <span className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-bold">Chase Escort</span>}
                 {load.needs_height_pole && <span className="px-3 py-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-xs font-bold">Height Pole</span>}
                 {load.needs_steersman && <span className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-bold">Steersman</span>}
                 {!load.needs_front_escort && !load.needs_chase_escort && !load.needs_height_pole && !load.needs_steersman && (
                   <span className="text-xs text-gray-400 italic">None specified.</span>
                 )}
              </div>
            </div>
          </div>

          {/* Pay & Rates — same component drivers see, so both sides always match */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Pay & Allowances</p>
            <LoadPayBreakdown load={load} variant="company" />
          </div>

          {/* Notes */}
          {load.notes && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Instructions / Notes</p>
              <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-3 sm:p-4">
                <p className="text-xs text-gray-800 leading-relaxed whitespace-pre-wrap">{load.notes}</p>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button onClick={onClose} className="border border-gray-200 text-gray-700 text-sm font-bold px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl hover:bg-gray-100 transition-colors">
            Close
          </button>
          <button 
            onClick={() => {
              onClose();
              if (isOpen) {
                navigate(`/loads/${load.pk || load.id}/applicants`);
              }
            }}
            className="text-white text-sm font-bold px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl transition-colors hover:opacity-90 shadow-sm whitespace-nowrap" 
            style={{ background:"#1a2744" }}
          >
            {isOpen ? "View Applicants" : "Driver Details"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── LOAD CARD ────────────────────────────────────────────────────────────────

function LoadCard({ load, onOpen, onInvite }) {
  const isOpen = load.status?.toLowerCase() === "open";
  
  const origin = load.from || load.origin || load.origin_label || "—";
  const destination = load.to || load.destination || load.destination_label || "—";
  const date = load.date || load.departure_date || load.pickup_date;
  const commodity = load.type || load.commodity || load.load_type || "—";
  
  const { main: fee, sub: feeSub } = payHeadline(load);

  return (
    <div
      onClick={() => onOpen(load)}
      className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group flex flex-col"
    >
      {/* Status + badges */}
      <div className="flex flex-wrap gap-1.5 mb-2.5">
        <StatusPill status={load.status} isOpen={isOpen}/>
        {load.featured && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
            <Star className="w-2.5 h-2.5"/> Featured
          </span>
        )}
      </div>

      {/* Route */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-sm font-bold text-gray-900 truncate">{origin}</span>
        <ArrowRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" strokeWidth={2.5}/>
        <span className="text-sm font-bold text-gray-900 truncate">{destination}</span>
      </div>

      {/* Meta */}
      <p className="text-xs text-gray-400 flex items-center gap-1.5 flex-1">
        <Truck className="w-3 h-3 flex-shrink-0"/>
        <span className="truncate">{commodity}</span>
        <span className="text-gray-200">•</span>
        {fmtDate(date)}
      </p>

      {/* Bottom row */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 gap-2">
        <div>
          <p className="text-[9px] text-gray-400 font-medium uppercase tracking-wider">Driver Pay</p>
          <p className="text-sm font-bold text-gray-900 mt-0.5">{fee}</p>
          {feeSub ? <p className="text-[10px] text-gray-400 mt-0.5">{feeSub}</p> : null}
        </div>
        <div className="flex items-center gap-1.5">
          {isOpen && (
            <button
              onClick={e => { e.stopPropagation(); onInvite(load); }}
              className="text-xs font-bold px-2.5 py-1.5 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
            >
              Invite Drivers
            </button>
          )}
          <button
            onClick={e => { e.stopPropagation(); onOpen(load); }}
            className="text-xs font-bold px-3 py-1.5 rounded-xl transition-all"
            style={isOpen
              ? { border:"1px solid #e5e7eb", background:"#fff", color:"#374151" }
              : { background:"#1a2744", color:"#fff" }
            }
          >
            {isOpen ? "Details" : "Driver Info"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-gray-200 rounded-2xl bg-white col-span-full">
      <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-4">
        <MapPin className="w-8 h-8 text-gray-300"/>
      </div>
      <h3 className="text-base font-bold text-gray-800 mb-2">No Loads Found</h3>
      <p className="text-sm text-gray-400 max-w-sm leading-relaxed mb-6">
        You haven't posted any loads matching this filter yet, or your load history is empty.
      </p>
      <Link to="/post-load">
        <button className="text-white text-sm font-bold px-5 py-3 rounded-xl flex items-center gap-2 hover:opacity-90 transition-opacity" style={{ background:"#1a2744" }}>
          <Plus className="w-4 h-4"/> Post Your First Load
        </button>
      </Link>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function MyLoads() {
  const navigate = useNavigate();
  const location = useLocation();

  const user  = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");

  const [loads,         setLoads]         = useState([]);
  const [isLoading,     setIsLoading]     = useState(true);
  const [activeNav,     setActiveNav]     = useState("loads");
  const [loadFilter,    setLoadFilter]    = useState("all");
  const [searchQuery,   setSearchQuery]   = useState("");
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [notifCount,    setNotifCount]    = useState(2);
  
  // Modals
  const [selectedLoad,  setSelectedLoad]  = useState(null); 
  const [inviteLoad,    setInviteLoad]    = useState(null);


  const handleLogout = () => {
    localStorage.clear();
    navigate("/company-login");
  };

  // Auth guard
  useEffect(() => {
    if (!user || !token) navigate("/company-login", { replace: true });
  }, [user, token, navigate]);

  // Fetch loads
  useEffect(() => {
    if (!user?.email || !token) { setIsLoading(false); return; }
    const go = async () => {
      try {
        const res = await fetch(`${API_URL}/api/loads/company/?email=${user.email}`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (res.ok) setLoads(data.results || data || []);
      } catch (err) { console.error(err); }
      finally { setIsLoading(false); }
    };
    go();
  }, [user?.email, token]);

  const companyName = user?.company_name || user?.name || "Company";
  const initials    = companyName.split(" ").slice(0,2).map(w=>w[0]).join("").toUpperCase() || "CO";

  const activePostings = loads.filter(l=>l.status?.toLowerCase()==="open").length;
  const assignedLoads  = loads.filter(l=>l.status?.toLowerCase()!=="open").length;
  const totalSpent     = loads.reduce((a,l)=>a+parseFloat(l.posting_fee || l.pay_amount || 0),0);

  const filteredLoads = loads.filter(load => {
    const matchFilter =
      loadFilter==="all"      ? true :
      loadFilter==="open"     ? load.status?.toLowerCase()==="open" :
      load.status?.toLowerCase()!=="open";
      
    const q = searchQuery.toLowerCase();
    
    // Safely check properties before calling toLowerCase()
    const matchSearch = !q ||
      (load.origin       && String(load.origin).toLowerCase().includes(q))       ||
      (load.destination  && String(load.destination).toLowerCase().includes(q))  ||
      (load.commodity    && String(load.commodity).toLowerCase().includes(q))    ||
      (load.type         && String(load.type).toLowerCase().includes(q))         ||
      (load.id           && String(load.id).includes(q))                         ||
      (load.reference_code && String(load.reference_code).toLowerCase().includes(q));
      
    return matchFilter && matchSearch;
  });

  if (!user || !token) return null;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden" style={{ fontFamily:"Inter,system-ui,sans-serif" }}>
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={()=>setSidebarOpen(false)}/>
      )}

      <Sidebar
        activeNav={activeNav} setActiveNav={setActiveNav}
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        initials={initials} companyName={companyName}
        loads={loads} activePostings={activePostings}
        assignedLoads={assignedLoads} totalSpent={totalSpent}
        handleLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar
          title="My Loads"
          setSidebarOpen={setSidebarOpen}
          initials={initials}
          notifCount={notifCount}
          setNotifCount={setNotifCount}
          handleLogout={handleLogout}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Manage Postings</h1>
                <p className="text-sm text-gray-500 mt-1">View, filter, and invite drivers for each dispatched load.</p>
              </div>
              <Link to="/post-load" className="flex-shrink-0">
                <button
                  className="w-full sm:w-auto flex items-center justify-center gap-2 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:-translate-y-0.5 transition-all"
                  style={{ background:"#1a2744", boxShadow:"0 4px 14px 0 rgba(26,39,68,0.2)" }}
                >
                  <Plus className="w-4 h-4"/> Post New Load
                </button>
              </Link>
            </div>

            {/* Filter + Search bar */}
            <div className="bg-white p-2 rounded-2xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-2">
              <div className="flex gap-1 overflow-x-auto p-1">
                {[
                  { key:"all",      label:"All Loads"      },
                  { key:"open",     label:"Pending Match"  },
                  { key:"assigned", label:"Assigned"       },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={()=>setLoadFilter(f.key)}
                    className="flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all"
                    style={loadFilter===f.key ? {background:"#1a2744",color:"#fff"} : {background:"transparent",color:"#6b7280"}}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="hidden sm:block w-px bg-gray-100 mx-1"/>
              <div className="flex-1 relative flex items-center">
                <Search className="w-4 h-4 text-gray-400 absolute left-3"/>
                <input
                  type="text"
                  placeholder="Search by city, state, or commodity…"
                  value={searchQuery}
                  onChange={e=>setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-blue-100 outline-none placeholder:text-gray-400"
                />
              </div>
            </div>

            {/* Stats summary */}
            {loads.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label:"Total Loads", val:loads.length, color:"text-gray-900" },
                  { label:"Pending Match", val:activePostings, color:"text-amber-600" },
                  { label:"Assigned", val:assignedLoads, color:"text-green-600" },
                ].map(s=>(
                  <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-4 py-3 text-center shadow-sm">
                    <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Load grid */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500"/>
                <p className="text-sm text-gray-400 font-medium">Retrieving your loads…</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredLoads.length === 0
                  ? <EmptyState/>
                  : filteredLoads.map(load => (
                      <LoadCard 
                        key={load.id} 
                        load={load} 
                        onOpen={setSelectedLoad}
                        onInvite={setInviteLoad}
                      />
                    ))
                }
              </div>
            )}
          </div>
        </main>

        <BottomNav activeNav={activeNav} setSidebarOpen={setSidebarOpen} handleLogout={handleLogout}/>
      </div>

      {/* Load Detail Modal */}
      {selectedLoad && (
        <LoadModal
          load={selectedLoad}
          onClose={()=>setSelectedLoad(null)}
        />
      )}

      {/* Invite Verified Drivers Modal */}
      {inviteLoad && (
        <InviteDriversModal
          load={inviteLoad}
          onClose={() => setInviteLoad(null)}
        />
      )}
    </div>
  );
}